import { Checkbox, Icon, Loading } from '@smartgift/frontend-toolkit';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import * as TableS from '@hero/components/molecules/Table/style';
import { contactServices } from '@hero/services';
import { cleanObjectEmptyFields } from '@hero/utils';

import * as S from '../Advance/style';

function CustomSelectionHead({ table }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [toggleSelectAll, setToggleSelectAll] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const globalFilters = useMemo(() => {
    return table.getState()?.globalFilters;
  }, [table.getState()?.globalFilters]);

  const rowSelection = useMemo(() => {
    return table.getState()?.rowSelection;
  }, [table.getState()?.rowSelection]);

  const totalRowCount = useMemo(() => {
    return table.getState()?.pagination?.total;
  }, [table.getState()?.pagination?.total]);

  const disabledRows = useMemo(() => {
    return table.getState()?.disabledRows;
  }, [table.getState()?.disabledRows]);

  const selectedRowCount = useMemo(() => {
    return Object.keys({ ...(rowSelection || {}) }).length;
  }, [rowSelection]);

  const addRowCount = useMemo(() => {
    return totalRowCount - Object.keys({ ...(disabledRows || {}), ...(rowSelection || {}) }).length;
  }, [disabledRows, rowSelection, totalRowCount]);

  const onToggleSelectAll = async () => {
    if (toggleSelectAll) {
      table.resetRowSelection();
    } else {
      const searchTerm = globalFilters.searchTerm;
      const tags = globalFilters.filters?.map(filter => filter.value);

      setIsLoading(true);
      const data = await queryClient.fetchQuery(['selectAll', searchTerm, tags], () => {
        return contactServices.selectAll(
          cleanObjectEmptyFields({
            filters: {
              searchTerm: globalFilters.searchTerm,
              tags: globalFilters.filters?.map(filter => filter.value)
            }
          })
        );
      });

      if (data?.docs?.length)
        table.setRowSelection(
          data.docs
            .filter(doc => !disabledRows[doc.id])
            .reduce((result, doc) => ({ ...result, [doc.id]: true }), {})
        );

      setIsLoading(false);
    }

    setToggleSelectAll(!toggleSelectAll);
  };

  React.useEffect(() => {
    if (selectedRowCount === 0) {
      setToggleSelectAll(false);
    }
    if (selectedRowCount === totalRowCount) {
      setToggleSelectAll(true);
    }
  }, [selectedRowCount, totalRowCount]);

  const dRows = table.getRowModel().rows.filter(row => disabledRows[row.id]);
  const rowLength = table.getRowModel().rows.length;
  const disabled = rowLength === dRows.length;
  const checkedLength = table
    .getRowModel()
    .rows.filter(row => !disabledRows[row.id] && rowSelection[row.id]).length;
  const checked = checkedLength === rowLength - dRows.length;
  const indeterminate = checkedLength > 0 && checkedLength < rowLength - dRows.length;

  return (
    <TableS.HeadSelected>
      <TableS.HeadCheckbox>
        <Checkbox
          {...{
            key: table.getState()?.pagination?.pageIndex,
            checked: disabled || checked,
            disabled,
            indeterminate,
            onChange: e => {
              table.toggleAllPageRowsSelected(e.target.checked);
            }
          }}
        />
      </TableS.HeadCheckbox>
      <TableS.HeadSelectedItems>
        <TableS.HeadSelectedItems>
          <Icon name="contacts" px={32} space={7} />
          <S.SelectedContactText>
            {t(toggleSelectAll ? 'nContactAreSelected' : 'selectedRowCount', {
              selectedRowCount: selectedRowCount,
              selectedTypo: selectedRowCount,
              totalContactCount: totalRowCount
            })}
          </S.SelectedContactText>
        </TableS.HeadSelectedItems>
        <S.SelectAllArea>
          {!toggleSelectAll && addRowCount > 0 && table.getPageCount() > 1 && (
            <S.SelectAllContactButton color="neutral" variant="link" onClick={onToggleSelectAll}>
              {t(toggleSelectAll ? 'clearSelection' : 'selectRemainingNType', {
                totalContactCount: addRowCount,
                type: t('contacts').toLocaleLowerCase()
              })}
            </S.SelectAllContactButton>
          )}

          {isLoading && <Loading size="sm" type="ring" />}
        </S.SelectAllArea>
      </TableS.HeadSelectedItems>
    </TableS.HeadSelected>
  );
}

export { CustomSelectionHead };
